import { Binding, BoneTypeEnum, BridgeBase, CaseModel, MainBase, RotateCameraType, SceneBase, SelectedApexMech, StateTypes, ViewType } from "@ortho-next/nextray-core";
import { EoCPlaneConfig } from "../Config/EoCPlaneConfig";
import { MultiViewsElementCustom } from "../Controls/MultiViewsElementCustom";
import { AppModel } from "../Models/AppModel";
import { BindedModel, bindedModel } from "../Models/BindedModel";
import { Context } from "../States/Context";
import { PrintUtils } from "../Utils/PrintUtils";
import { Bridge } from "./Bridge";
import { ContextInit } from "./ContextInit";
import { Layers } from "./Layers";
import { MeasuresController } from "./MeasuresController";
import { SaveHandler } from "./Save";
import { Tools, ToolsAP, ToolsLT } from "./Tools";
import { ToolsInit } from "./ToolsInit";

export type Scene = SceneBase<Tools>;

/**
 * Manages rendering and all logics.
 */
export class Main extends MainBase {
	public declare scenes: Scene[];
	public declare model: AppModel;
	public declare bridge: Bridge;
	public declare caseModel: CaseModel;
	public declare toolsInit: ToolsInit;
	public declare printUtils: PrintUtils;
	public declare layers: Layers;
	public declare measuresController: MeasuresController;
	protected declare _context: Context;
	protected declare _saveHandler: SaveHandler;

	//TODO improve override def?
	public override get activeScene(): Scene { return super.activeScene as Scene }
	public override get toolsAP(): ToolsAP { return super.toolsAP as ToolsAP }
	public override get toolsLT(): ToolsLT { return super.toolsLT as ToolsLT }
	public override get activeTools(): Tools { return super.activeTools as Tools }

	public get isPostOp(): boolean {
		return this.caseModel.isPostOperative;
	}

	constructor(canvasContainer: HTMLDivElement) {
		super(canvasContainer, {
			appModel: AppModel,
			bindedModel: new BindedModel(),
			bridge: Bridge as typeof BridgeBase,
			contextProto: Context,
			measuresController: MeasuresController,
			printUtilsProto: PrintUtils,
			saveHandlerProto: SaveHandler,
			sceneProto: SceneBase,
			toolsAPProto: ToolsAP,
			toolsLTProto: ToolsLT
		});
		EoCPlaneConfig.init();
		ContextInit.create(this, this._context);
		this.showStats = false; //todo remove it from package
	}

	/**
	 * Rotates camera based on app state.
	 */
	public rotateCamera(type: RotateCameraType): void { //move RotateCameraType out of pacchetto
		for (const scene of this.scenes) {
			// TODO: refactor
			if ((scene.meshes.viewType === ViewType.AP && this.hasAP) || (scene.meshes.viewType === ViewType.LT && this.hasLT)) {
				const value = type === RotateCameraType.none ? 0 : -scene.meshes.clonedMechanicalAxis!.getAngleForTemplating(this.model.selectedApex as SelectedApexMech);
				scene.cameraControls.rotateCamera(value);
			}
		}
		for (const viewport of this.multiviewRenderer.viewsports as MultiViewsElementCustom[]) {
			if ((viewport.scene.meshes.viewType === ViewType.AP && this.hasAP) || (viewport.scene.meshes.viewType === ViewType.LT && this.hasLT)) {
				const value = type === RotateCameraType.none ? 0 : -viewport.scene.meshes.clonedMechanicalAxis!.getAngleForTemplating(this.model.selectedApex as SelectedApexMech);
				viewport.cameraControls.rotateCamera(value);
			}
		}
	}

	public rotateCameraByState(): void {
		if (this.model.stateType === StateTypes.templating) {
			this.rotateCamera(RotateCameraType.templating);
		} else if (this.model.stateType !== StateTypes.loading) {
			this.rotateCamera(RotateCameraType.none);
		}
	}

	public override async loadScenes(caseModel: CaseModel): Promise<void> {
		if (!this.caseModel) {
			this.initBoneTypeLT(caseModel);
			await super.loadScenes(caseModel, ToolsInit, Layers);
			this.rotateCameraByState();
			this.setView(this.hasAP ? ViewType.AP : ViewType.LT);
			this._context.handle("setInitialState", caseModel);
		}
	}

	private initBoneTypeLT(caseModel: CaseModel): void {
		if (caseModel.imgLT) {
			Binding.createCustom("boneTypeLT", (m: BindedModel) => m.boneTypeLT!, (value: BoneTypeEnum) => {
				this.model.boneTypeLT = value;
			}, ["boneTypeLT"]);
			bindedModel.boneTypeLT = caseModel.boneType;
		}
	}

	public override getTools(viewType: ViewType): Tools {
		return super.getTools(viewType) as Tools;
	}
}
